export const DILIGENCE_DOCUMENT_RUN_STAGES = [
  'received',
  'transcribing_audio',
  'searching_web',
  'searching_doctrine',
  'reading_chats',
  'working_diligence',
  'preparing_attachments',
  'listing_templates',
  'loading_template',
  'listing_documents',
  'restoring_document',
  'inspecting_attachment',
  'saving_template',
  'updating_template',
  'preparing_brief',
  'starting_document',
  'delivering_text',
  'preparing_workspace',
  'inspecting_template',
  'planning_edits',
  'applying_edits',
  'rendering_document',
  'reviewing_pages',
  'repairing_document',
  'finalizing_artifact',
  'completed',
  'failed',
  'cancelled'
] as const;

export type DiligenceDocumentRunStage = typeof DILIGENCE_DOCUMENT_RUN_STAGES[number];

export interface DiligenceDocumentRunEvent {
  sequence: number;
  stage: DiligenceDocumentRunStage;
  label: string;
  status: 'completed' | 'active' | 'failed' | 'cancelled';
  atMs: number;
  attempt?: number;
  pageCount?: number;
  reviewedPages?: number;
}

export interface DiligenceDocumentRunProgress {
  version: 1;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  stage: DiligenceDocumentRunStage;
  startedAtMs: number;
  finishedAtMs?: number;
  timingComplete: boolean;
  attemptStartedAtMs?: number;
  documentaryWorkStarted?: boolean;
  events: DiligenceDocumentRunEvent[];
}

const MAX_VISIBLE_EVENTS = 32;

const LABELS: Record<DiligenceDocumentRunStage, string> = {
  transcribing_audio: 'Transcribiendo el audio.',
  searching_web: 'Consultando fuentes web.',
  searching_doctrine: 'Consultando la base de doctrina.',
  reading_chats: 'Leyendo los chats mencionados.',
  working_diligence: 'Trabajando en la documentación.',
  preparing_attachments: 'Preparando los documentos adjuntos.',
  listing_templates: 'Consultando tus plantillas.',
  loading_template: 'Cargando la plantilla seleccionada.',
  listing_documents: 'Consultando los documentos preparados.',
  restoring_document: 'Recuperando el documento seleccionado.',
  inspecting_attachment: 'Comprobando el documento adjunto.',
  saving_template: 'Guardando la plantilla en tu biblioteca.',
  updating_template: 'Actualizando la plantilla.',
  preparing_brief: 'Preparando los datos y decisiones de la documentación.',
  starting_document: 'Iniciando la generación del documento.',
  delivering_text: 'Preparando el contenido para entregarlo.',
  received: 'Trabajo recibido; la generación continuará automáticamente.',
  preparing_workspace: 'Preparando el espacio de trabajo del documento.',
  inspecting_template: 'Comprobando la plantilla.',
  planning_edits: 'Organizando el contenido del documento.',
  applying_edits: 'Incorporando el contenido confirmado.',
  rendering_document: 'Preparando las páginas para revisarlas.',
  reviewing_pages: 'Revisando el documento página a página.',
  repairing_document: 'Corrigiendo un problema detectado en el documento.',
  finalizing_artifact: 'Comprobando el archivo generado.',
  completed: 'Documento revisado y listo para descargar.',
  failed: 'La generación se ha detenido; el trabajo confirmado se conserva.',
  cancelled: 'Has detenido esta generación; el trabajo confirmado se conserva.'
};

export function createInitialDocumentRun(atMs = Date.now()): DiligenceDocumentRunProgress {
  return {
    version: 1,
    status: 'running',
    stage: 'received',
    startedAtMs: atMs,
    timingComplete: true,
    documentaryWorkStarted: true,
    events: [{
      sequence: 1,
      stage: 'received',
      label: LABELS.received,
      status: 'active',
      atMs
    }]
  };
}

// Same server-owned contract for conversation and Workflow. Waiting is UI-only.
export function createInitialTurnRun(atMs = Date.now()): DiligenceDocumentRunProgress {
  return { ...createInitialDocumentRun(atMs), documentaryWorkStarted: false, events: [] };
}

export function finishTurnRun(current: DiligenceDocumentRunProgress, atMs = Date.now(),
  status: 'completed' | 'failed' | 'cancelled' = 'completed'): DiligenceDocumentRunProgress {
  return { ...current, status, finishedAtMs: atMs,
    events: current.events.map((event) => event.status === 'active'
      ? { ...event, status: status === 'completed' ? 'completed' : status } : event) };
}

export function finishRunActivity(current: DiligenceDocumentRunProgress,
  stage: DiligenceDocumentRunStage, status: 'completed' | 'failed' | 'cancelled' = 'completed',
  sequence?: number): DiligenceDocumentRunProgress {
  return { ...current, events: current.events.map((event) => event.stage === stage && event.status === 'active'
    && (sequence === undefined || event.sequence === sequence)
    ? { ...event, status } : event) };
}

// Terminal stages alone do not prove what a historical conversational turn did.
export function isDocumentaryActivity(stage: DiligenceDocumentRunStage) {
  return DILIGENCE_DOCUMENT_RUN_STAGES.includes(stage)
    && !['received', 'transcribing_audio', 'searching_web', 'searching_doctrine', 'reading_chats',
    'completed', 'failed', 'cancelled'].includes(stage);
}

export function getToolActivityStage(name: string): DiligenceDocumentRunStage | undefined {
  const stages: Record<string, DiligenceDocumentRunStage> = {
    listar_plantillas: 'listing_templates', cargar_plantilla: 'loading_template',
    inspeccionar_adjunto: 'inspecting_attachment', actualizar_plantilla: 'updating_template',
    guardar_adjunto_como_plantilla: 'saving_template',
    preparar_diligencia: 'preparing_brief', preparar_diligencia_simple: 'preparing_brief',
    preparar_conjunto_documental: 'preparing_brief',
    listar_documentos_preparados: 'listing_documents', recuperar_documento_preparado: 'restoring_document',
    renderizar_diligencia: 'starting_document',
    entregar_contenido_sin_formato: 'delivering_text'
  };
  return Object.hasOwn(stages, name) ? stages[name] : undefined;
}

export function getToolActivityOutcome(result: string): 'completed' | 'failed' {
  try {
    const value = JSON.parse(result);
    return value && typeof value === 'object' && value.ok !== false ? 'completed' : 'failed';
  } catch { return 'failed'; }
}

function safeCounter(value: unknown) {
  return Number.isInteger(value) && Number(value) >= 0 ? Number(value) : undefined;
}

export function parseDocumentRun(value: unknown): DiligenceDocumentRunProgress {
  if (!value || typeof value !== 'object') return createInitialDocumentRun();
  const raw = value as Record<string, unknown>;
  const stages = new Set<string>(DILIGENCE_DOCUMENT_RUN_STAGES);
  const parsedEvents = Array.isArray(raw.events) ? raw.events.flatMap((event) => {
    if (!event || typeof event !== 'object') return [];
    const item = event as Record<string, unknown>;
    if (!stages.has(String(item.stage)) || typeof item.label !== 'string'
      || !['completed', 'active', 'failed', 'cancelled'].includes(String(item.status))) return [];
    const sequence = safeCounter(item.sequence);
    const atMs = safeCounter(item.atMs);
    if (sequence === undefined || atMs === undefined) return [];
    return [{
      sequence,
      stage: item.stage as DiligenceDocumentRunStage,
      label: item.label.slice(0, 180),
      status: item.status as DiligenceDocumentRunEvent['status'],
      atMs,
      ...(safeCounter(item.attempt) !== undefined ? { attempt: safeCounter(item.attempt) } : {}),
      ...(safeCounter(item.pageCount) !== undefined ? { pageCount: safeCounter(item.pageCount) } : {}),
      ...(safeCounter(item.reviewedPages) !== undefined ? { reviewedPages: safeCounter(item.reviewedPages) } : {})
    }];
  }) : [];
  const events = parsedEvents.slice(-MAX_VISIBLE_EVENTS);
  const stage = stages.has(String(raw.stage))
    ? raw.stage as DiligenceDocumentRunStage
    : events.at(-1)?.stage ?? 'received';
  const status = raw.status === 'completed' || raw.status === 'failed' || raw.status === 'cancelled'
    ? raw.status : 'running';
  const fallback = createInitialDocumentRun();
  const startedAtMs = safeCounter(raw.startedAtMs) ?? parsedEvents.at(0)?.atMs ?? fallback.startedAtMs;
  const finishedAtMs = safeCounter(raw.finishedAtMs)
    ?? (status === 'running' ? undefined : parsedEvents.at(-1)?.atMs);
  const timingComplete = raw.timingComplete === true
    || (raw.timingComplete !== false && parsedEvents.at(0)?.sequence === 1);
  return {
    version: 1,
    status,
    stage,
    startedAtMs,
    ...(finishedAtMs !== undefined ? { finishedAtMs } : {}),
    timingComplete,
    ...(safeCounter(raw.attemptStartedAtMs) !== undefined ? { attemptStartedAtMs: safeCounter(raw.attemptStartedAtMs) } : {}),
    ...(raw.documentaryWorkStarted === true || parsedEvents.some(event => isDocumentaryActivity(event.stage))
      || isDocumentaryActivity(stage) ? { documentaryWorkStarted: true }
      : raw.documentaryWorkStarted === false ? { documentaryWorkStarted: false } : {}),
    events: Array.isArray(raw.events) ? events : fallback.events
  };
}

export function advanceDocumentRun(
  current: unknown,
  stage: DiligenceDocumentRunStage,
  data: {
    attempt?: number;
    pageCount?: number;
    reviewedPages?: number;
    atMs?: number;
    independent?: boolean;
  } = {}
): DiligenceDocumentRunProgress {
  const parsed = parseDocumentRun(current);
  if (parsed.status !== 'running' && stage !== parsed.stage) return parsed;
  const terminal = stage === 'completed' || stage === 'failed' || stage === 'cancelled';
  const last = parsed.events.at(-1);
  const sameActiveStage = !terminal && !data.independent && last?.stage === stage && last.status === 'active';
  const attempt = safeCounter(data.attempt) ?? (sameActiveStage ? last.attempt : undefined);
  const pageCount = safeCounter(data.pageCount) ?? (sameActiveStage ? last.pageCount : undefined);
  const reviewedPages = safeCounter(data.reviewedPages)
    ?? (sameActiveStage ? last.reviewedPages : undefined);
  if (terminal && last?.stage === stage
    && parsed.status === stage) return parsed;
  if (sameActiveStage
    && last.attempt === attempt && last.pageCount === pageCount
    && last.reviewedPages === reviewedPages) return parsed;

  if (sameActiveStage && last) {
    return {
      ...parsed,
      events: [
        ...parsed.events.slice(0, -1),
        {
          ...last,
          ...(attempt !== undefined ? { attempt } : {}),
          ...(pageCount !== undefined ? { pageCount } : {}),
          ...(reviewedPages !== undefined ? { reviewedPages } : {})
        }
      ]
    };
  }

  const events = parsed.events.map((event) => (
    event.status === 'active' && !data.independent ? { ...event, status: 'completed' as const } : event
  ));
  const next: DiligenceDocumentRunEvent = {
    sequence: Math.max(0, ...events.map((event) => event.sequence)) + 1,
    stage,
    label: LABELS[stage],
    status: stage === 'failed' ? 'failed' : stage === 'cancelled' ? 'cancelled'
      : terminal ? 'completed' : 'active',
    atMs: safeCounter(data.atMs) ?? Date.now(),
    ...(attempt !== undefined ? { attempt } : {}),
    ...(pageCount !== undefined ? { pageCount } : {}),
    ...(reviewedPages !== undefined ? { reviewedPages } : {})
  };
  return {
    version: 1,
    status: stage === 'completed' ? 'completed' : stage === 'failed' ? 'failed'
      : stage === 'cancelled' ? 'cancelled' : 'running',
    stage,
    startedAtMs: parsed.startedAtMs,
    ...(terminal ? { finishedAtMs: next.atMs } : {}),
    timingComplete: parsed.timingComplete,
    ...(parsed.attemptStartedAtMs !== undefined ? { attemptStartedAtMs: parsed.attemptStartedAtMs } : {}),
    ...(parsed.documentaryWorkStarted !== undefined || isDocumentaryActivity(stage)
      ? { documentaryWorkStarted: parsed.documentaryWorkStarted === true || isDocumentaryActivity(stage) } : {}),
    events: [...events, next].slice(-MAX_VISIBLE_EVENTS)
  };
}

/** Append only events observed from the current member's private run. Its
 * cursor lives on the job, while the public group owns a monotonic sequence. */
export function mergeBundleDocumentRun(groupValue: unknown, memberValue: unknown,
  reportedSequence = 0, finishGroup = false): DiligenceDocumentRunProgress {
  const local = parseDocumentRun(memberValue);
  const group = groupValue ? parseDocumentRun(groupValue) : { ...local, events: [] };
  let events = group.events.map(event => ({ ...event }));
  for (const event of local.events.filter(event => event.sequence > reportedSequence)) {
    events = events.map(previous => previous.status === 'active' ? { ...previous, status: 'completed' as const } : previous);
    events.push({ ...event, sequence: Math.max(0, ...events.map(previous => previous.sequence)) + 1 });
  }
  // A page-count update can change the active local event without allocating a
  // new event. Preserve its timestamp and public sequence when refreshing it.
  const localLast = local.events.at(-1); const groupLast = events.at(-1);
  if (localLast && groupLast && localLast.sequence <= reportedSequence && localLast.stage === groupLast.stage) {
    events[events.length - 1] = { ...localLast, sequence: groupLast.sequence, atMs: groupLast.atMs };
  }
  const { finishedAtMs, ...base } = group;
  void finishedAtMs;
  return { ...base, status: finishGroup ? local.status : 'running', stage: local.stage,
    ...(finishGroup && local.finishedAtMs !== undefined ? { finishedAtMs: local.finishedAtMs } : {}),
    documentaryWorkStarted: true, events: events.slice(-MAX_VISIBLE_EVENTS) };
}

export function mapRuntimePhaseToDocumentStage(
  phase: string,
  jobPhase: 'initial' | 'repair' | 'final_repair'
): DiligenceDocumentRunStage | null {
  switch (phase) {
    case 'sandbox_create': return 'preparing_workspace';
    case 'source_inventory': return 'inspecting_template';
    case 'planning': return jobPhase === 'repair' ? 'repairing_document' : 'planning_edits';
    case 'pdf_check_layout': return jobPhase === 'initial' ? 'planning_edits' : 'repairing_document';
    case 'repair': return 'repairing_document';
    case 'document_planning': return 'applying_edits';
    case 'document_repair': return 'repairing_document';
    case 'docx_edit_plan': return 'applying_edits';
    case 'pdf_apply_edits': return 'applying_edits';
    case 'materialize':
    case 'rendering_pages': return 'rendering_document';
    case 'visual_qa':
    case 'reviewing_pages': return 'reviewing_pages';
    case 'artifact_read': return 'finalizing_artifact';
    default: return null;
  }
}
